import axios from "axios";
import React, { useEffect, useState, Suspense, useMemo } from "react";
import {
  FiCornerDownLeft,
  FiEdit,
  FiLink,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Input,
  Button,
  Table,
  Spinner,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  InputGroup,
  InputGroupText,
  Collapse,
  Card,
  CardBody,
  CardTitle,
} from "reactstrap";
import {
  getInventorySupplier,
  getInventoryPurchase,
  getInventoryItems,
} from "../../../../redux/inventory";
import { useNavigate } from "react-router-dom";
import PurchaseForm from "./PurchaseForm";
import PaginatedList from "../PaginatedList";
import InvoiceItemsPopover from "./InvoiceItemsPopover";
import PurchaseFilterForm from "./PurchaseFilterForm";
import { formatDate } from "../../../utils/dateFormatter";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const PurchaseDashboard = React.lazy(() => import("./PurchaseDashboard"));

const Purchase = () => {
  const [showSummary, setShowSummary] = useState(false);
  const itemsList = useSelector((state) => state.inventory);
  const dispatch = useDispatch();
  const { supplierData, purchaseData, itemsData } = itemsList || {};
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;

  const [formData, setFormData] = useState("");
  const [modal, setModal] = useState(false);
  const [modalItems, setModalItems] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const navigate = useNavigate();
  const [popoverData, setPopoverData] = useState("");
  const toggle = () => setModal(!modal);
  const toggleModalItems = () => setModalItems(!modalItems);
  const [mode, setMode] = useState("");
  const [purchasePaginatedList, setPurchasePaginatedList] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);
  const [filters, setFilters] = useState({});
  const [refresh, setRefresh] = useState(false);

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };
  const toggleShow = () => setShowSummary(!showSummary);

  const editSupplierHandler = (Data, toggle, type) => {
    toggle();
    setMode(type);
    setFormData(Data);
  };

  const deleteHandler = (item, itemId) => {
    const slno = prompt("Please enter the SL No. to confirm delete");

    if (slno && Number(slno.trim()) === itemId) {
      axios
        .delete(`${process.env.REACT_APP_COLLEGE_ERP_API}delete-purchase`, {
          params: { id: item._id, branch: userData.branch },
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        })
        .then((res) => {
          if (res) {
            setRefresh((prev) => !prev);
            dispatch(
              getInventoryPurchase({
                token: userData.token,
                branch: userData.branch,
              })
            );
          }
        })
        .catch((err) => {
          if (
            (err.response?.data?.err?.name === "TokenExpiredError") ||
            err.response?.data?.message === "Unauthorized"
          ) {
            navigate("/");
            sessionStorage.removeItem("token");
            sessionStorage.removeItem("srchData");
            sessionStorage.removeItem("prescriptionHistory");
            sessionStorage.removeItem("rights");
            navigate("/cri-crm");
            return err;
          }
        });
    } else if (slno) {
      alert("Contact No. not matching");
    }
  };

  const supplierNameHandler = (supplierId) => {
    const supplier = supplierData?.find(
      (item) => item.supplierId === supplierId
    );
    if (supplier) return supplier.supplierName;
    return null;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    const lowerValue = value.toLowerCase();

    const matchedSupplier = supplierData?.find((s) =>
      s.supplierName?.toLowerCase().includes(lowerValue)
    );

    if (matchedSupplier) {
      setSearchValue(matchedSupplier.supplierId);
      setSearchInput(value);
    } else {
      setSearchInput(value);
      setSearchValue(value);
    }
  };

  // Compute graph data dynamically based on active search input matching purchase items
  const itemGraphData = useMemo(() => {
    if (!searchInput.trim() || !purchasePaginatedList?.length) return [];

    const lowerQuery = searchInput.toLowerCase();
    const itemMap = {};

    purchasePaginatedList.forEach((record) => {
      if (Array.isArray(record.invoice)) {
        record.invoice.forEach((invItem) => {
          if (invItem.item && invItem.item.toLowerCase().includes(lowerQuery)) {
            const key = `${invItem.item} (${invItem.unit || "Units"})`;
            const qty = parseFloat(invItem.quantity) || 0;

            if (!itemMap[key]) {
              itemMap[key] = {
                name: invItem.item,
                unit: invItem.unit || "Units",
                totalQuantity: 0,
              };
            }
            itemMap[key].totalQuantity += qty;
          }
        });
      }
    });

    return Object.values(itemMap);
  }, [searchInput, purchasePaginatedList]);

  useEffect(() => {
    if (userData.token && userData.branch)
      dispatch(
        getInventoryItems({
          token: userData.token,
          branch: userData.branch,
        })
      );
  }, [userData.token, userData.branch, dispatch]);

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col md="3" className="text-center">
          <h3>Purchase Details</h3>
        </Col>
      </Row>

      <Row>
        <Col md={2}>
          <Button
            color="primary"
            size="sm"
            onClick={toggleShow}
            className="m-1"
          >
            {showSummary ? "Hide Summary" : "Show Summary"}
          </Button>
        </Col>
      </Row>

      <Collapse isOpen={showSummary}>
        <div>
          <PurchaseFilterForm onFilter={handleFilter} />
          <Suspense fallback={<div>Loading Purchases...</div>}>
            <PurchaseDashboard
              filters={filters}
              branch={userData.branch}
              token={userData.token}
              supplierData={supplierData}
            />
          </Suspense>
        </div>
      </Collapse>

      <Row className="align-items-center pt-3">
        {/* Purchase Entry Button */}
        <Col md={2} className="mb-3">
          <Button
            color="primary"
            onClick={() => {
              setFormData("");
              toggle();
              setMode("");
            }}
          >
            Purchase Entry
          </Button>
        </Col>

        {/* Search Input */}
        <Col md={4} className="mb-3">
          <InputGroup>
            <Input
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search supplier, invoice ID, or item name..."
            />
            <InputGroupText>
              <FiSearch />
            </InputGroupText>
          </InputGroup>
        </Col>

        {/* Start Date Input */}
        <Col md={3} className="mb-3">
          <InputGroup>
            <InputGroupText>From</InputGroupText>
            <Input
              type="date"
              value={filters?.fromDate || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />
          </InputGroup>
        </Col>

        {/* End Date Input */}
        <Col md={3} className="mb-3">
          <InputGroup>
            <InputGroupText>To</InputGroupText>
            <Input
              type="date"
              value={filters?.toDate || ""}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Dynamic Item Quantity Graph */}
      {itemGraphData.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <CardBody>
                <CardTitle tag="h5">
                  Item Quantities Summary for "{searchInput}"
                </CardTitle>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={itemGraphData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value, name, props) => [`${value} ${props.payload.unit}`, "Purchased Qty"]} />
                      <Legend />
                      <Bar dataKey="totalQuantity" name="Total Quantity" fill="#007bff" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <>
        {/* Pass searchInput and filters directly */}
        <PaginatedList
          searchInput={searchValue}
          filters={filters}
          setShowSpinner={setShowSpinner}
          endPoint2="purchase-pagination"
          setPurchasePaginatedList={setPurchasePaginatedList}
          setTotalPaid={setTotalPaid}
          purchaseLimit={100}
          refresh={refresh}
        />

        {showSpinner && (
          <div className="text-center my-3">
            <Spinner color="primary" />
          </div>
        )}

        {!purchasePaginatedList || purchasePaginatedList.length === 0 ? (
          <div className="text-center mt-3">No Purchase Data Found</div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginBottom: "10px",
                fontWeight: "bold",
                fontSize: "1.1rem",
                color: "#2c3e50",
              }}
            >
              Total Paid:&nbsp;
              <span style={{ color: "green" }}>₹ {totalPaid.toFixed(2)}</span>
            </div>

            <Table responsive striped>
              <thead>
                <tr className="table-secondary">
                  <th style={{ width: "70px" }}>Sl No.</th>
                  <th style={{ width: "20%" }}>Supplier Name</th>
                  <th>Invoice Date</th>
                  <th>Invoice No</th>
                  <th>Amount Paid</th>
                  <th>Payment Mode</th>
                  <th>Items</th>
                  <th>Return</th>
                  <th>Edit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {purchasePaginatedList.map((item, id) => {
                  const supplierName =
                    supplierNameHandler(item?.supplier?.supplierId) || "N/A";
                  const invoiceDate = formatDate(
                    item?.paymentDetails?.invoice_date?.$date || item?.paymentDetails?.invoice_date
                  );
                  const invoiceId = item?.paymentDetails?.invoice_id || "-";
                  const amountPaid =
                    typeof item?.paymentDetails?.amount_paid === "number"
                      ? item.paymentDetails.amount_paid.toFixed(2)
                      : item?.paymentDetails?.amount_paid
                      ? parseFloat(item.paymentDetails.amount_paid).toFixed(2)
                      : "-";
                  const paymentMode = item?.paymentDetails?.payment_mode || "-";
                  const invoiceItems = Array.isArray(item?.invoice)
                    ? item.invoice
                    : [];

                  return (
                    <tr key={item._id?.$oid || item._id || id}>
                      <td style={{ textAlign: "center" }}>{id + 1}</td>
                      <td style={{ width: "200px" }}>{supplierName}</td>
                      <td>{invoiceDate}</td>
                      <td>{invoiceId}</td>
                      <td>{amountPaid}</td>
                      <td>{paymentMode}</td>
                      <td>
                        <FiLink
                          style={{ cursor: "pointer" }}
                          id={`invoiceItems-${id}`}
                          type="button"
                          onClick={() => setPopoverData(invoiceItems)}
                        />
                        <InvoiceItemsPopover
                          targetId={`invoiceItems-${id}`}
                          items={invoiceItems}
                        />
                      </td>
                      <td>
                        <FiCornerDownLeft
                          id="returnMode"
                          className="ms-2"
                          color="blue"
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            editSupplierHandler(item, toggle, "Return Mode")
                          }
                        />
                      </td>
                      <td>
                        {(userData.rights === "SuperAdmin" ||
                          userData.rights === "Manager" ||
                          userData.rights === "Accounts") && (
                          <FiEdit
                            color="green"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              editSupplierHandler(item, toggle, "Edit Mode")
                            }
                          />
                        )}
                      </td>
                      <td>
                        {(userData.rights === "SuperAdmin" ||
                          userData.rights === "Manager" ||
                          userData.rights === "Accounts") && (
                          <FiTrash2
                            color="red"
                            style={{ cursor: "pointer" }}
                            onClick={() => deleteHandler(item, id + 1)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </>
        )}
      </>

      <Modal
        isOpen={modal}
        toggle={toggle}
        fullscreen
        style={{ maxWidth: "100%" }}
      >
        <ModalHeader toggle={toggle}>{mode}</ModalHeader>
        <ModalBody>
          <PurchaseForm
            editFormData={formData}
            togglePurchaseEntry={toggle}
            mode={mode}
            itemsData={itemsData}
          />
        </ModalBody>
        <ModalFooter>
          <button onClick={toggle}>Close</button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default Purchase;